// shabba-dashboard/src/components/analytics/AttributionDashboard.tsx
// Real-time profit attribution showing WHERE profits come from

import React, { useMemo, useState } from 'react';
import { PieChart, DollarSign, TrendingUp, TrendingDown, Minus, Clock, Activity } from 'lucide-react';
import { useMarket } from '../../context/MarketContext';
import type { Trade } from '../../types';

interface Attribution {
  spreadCapture: number;     // Profit from spread
  timingAlpha: number;       // Profit from entry/exit timing
  marketMovement: number;    // Profit from market direction
  transactionCosts: number;  // DSE fees (0.3448%)
  slippage: number;          // Execution cost difference
  netPnL: number;           // Total net P&L
}

interface TradeMetrics {
  totalTrades: number;
  profitableTrades: number;
  winRate: number;
  avgProfitPerTrade: number;
  totalVolume: number;
  avgTradeSize: number;
}

interface AttributionDashboardProps {
  timeWindow?: number;  // Minutes of trade history to analyze
  positionSize?: number;
  showBreakdown?: boolean;
}

const DSE_TRANSACTION_COST_RATE = 0.003448; // 0.3448% DSE fees

export default function AttributionDashboard({
  timeWindow = 60,     // 1 hour
  positionSize = 300,
  showBreakdown = true
}: AttributionDashboardProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<'5m' | '15m' | '1h' | '4h' | '1d'>('1h');

  const {
    selectedSymbol,
    currentPrice,
    recentTrades,
    positions,
    labelForSecId,
    twapDataBySymbol
  } = useMarket();

  // Get TWAP from WebSocket data based on selected period
  const getTWAPForPeriod = useMemo(() => {
    const twapArray = twapDataBySymbol.get(selectedSymbol);
    if (!twapArray || twapArray.length === 0) return null;
    
    const latest = twapArray[twapArray.length - 1];
    // Map period to TWAP window
    const periodMap: Record<typeof selectedPeriod, 'twap1m' | 'twap5m' | 'twap15m' | 'twap30m' | 'twap1h' | 'twap4h'> = {
      '5m': 'twap5m',
      '15m': 'twap15m',
      '1h': 'twap1h',
      '4h': 'twap4h',
      '1d': 'twap4h' // Use 4h for 1d period
    };
    
    const twapKey = periodMap[selectedPeriod];
    return latest[twapKey] ?? latest.twap15m ?? latest.twap5m ?? latest.twap1m ?? null;
  }, [twapDataBySymbol, selectedSymbol, selectedPeriod]);

  // Calculate attribution metrics
  const attribution = useMemo((): { attribution: Attribution; metrics: TradeMetrics } | null => {
    if (!recentTrades || recentTrades.length < 5 || !currentPrice) {
      return null;
    }

    // Filter trades by time window
    const periodMinutes = {
      '5m': 5,
      '15m': 15,
      '1h': 60,
      '4h': 240,
      '1d': 1440
    }[selectedPeriod];

    const cutoffTime = new Date(Date.now() - periodMinutes * 60 * 1000);
    const windowTrades = recentTrades
      .filter(trade => new Date(trade.time) > cutoffTime)
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    if (windowTrades.length < 3) return null;

    // Use TWAP from WebSocket data for fair value reference
    const fairValue = getTWAPForPeriod ?? currentPrice;

    // Simulate market making trades
    let totalSpreadCapture = 0;
    let totalTimingAlpha = 0;
    let totalMarketMovement = 0;
    let totalTransactionCosts = 0;
    let totalSlippage = 0;
    let profitableTrades = 0;
    let totalVolume = 0;

    // Analyze each potential trade opportunity
    for (let i = 1; i < windowTrades.length - 1; i++) {
      const currentTrade = windowTrades[i];
      const price = currentTrade.price;
      const quantity = Math.min(positionSize, currentTrade.size);

      totalVolume += quantity;

      // Use current TWAP value (from WebSocket) as reference
      // For historical analysis, we use the current TWAP as approximation
      const twapAtTime = getTWAPForPeriod ?? price;

      // Determine if this would be a profitable market making opportunity
      const spreadThreshold = 0.015; // 1.5% spread (150 bps)
      const buyThreshold = twapAtTime * (1 - spreadThreshold);
      const sellThreshold = twapAtTime * (1 + spreadThreshold);

      let tradeProfit = 0;
      let spreadCaptureContrib = 0;
      let timingContrib = 0;
      let marketMovementContrib = 0;

      if (price <= buyThreshold) {
        // Simulated buy trade
        const targetSellPrice = twapAtTime * (1 + spreadThreshold);
        const actualExitPrice = i < windowTrades.length - 2 ?
          windowTrades[i + 1].price : currentPrice;

        // Spread capture: profit from TWAP-based pricing
        spreadCaptureContrib = (twapAtTime - price) * quantity;

        // Timing alpha: additional profit from optimal exit timing
        timingContrib = Math.max(0, (actualExitPrice - targetSellPrice) * quantity);

        // Market movement: profit from overall market direction
        marketMovementContrib = (actualExitPrice - twapAtTime) * quantity;

        tradeProfit = (actualExitPrice - price) * quantity;

      } else if (price >= sellThreshold) {
        // Simulated sell trade
        const targetBuyPrice = twapAtTime * (1 - spreadThreshold);
        const actualExitPrice = i < windowTrades.length - 2 ?
          windowTrades[i + 1].price : currentPrice;

        // Spread capture: profit from TWAP-based pricing
        spreadCaptureContrib = (price - twapAtTime) * quantity;

        // Timing alpha: additional profit from optimal exit timing
        timingContrib = Math.max(0, (targetBuyPrice - actualExitPrice) * quantity);

        // Market movement: profit from market direction (inverse for shorts)
        marketMovementContrib = (twapAtTime - actualExitPrice) * quantity;

        tradeProfit = (price - actualExitPrice) * quantity;
      }

      // Transaction costs
      const transactionCost = price * quantity * DSE_TRANSACTION_COST_RATE * 2; // Round trip

      // Slippage (difference between expected and actual execution)
      const expectedPrice = price;
      const actualPrice = price; // Assuming perfect execution for now
      const slippageCost = Math.abs(expectedPrice - actualPrice) * quantity;

      // Accumulate attribution
      totalSpreadCapture += spreadCaptureContrib;
      totalTimingAlpha += timingContrib;
      totalMarketMovement += marketMovementContrib;
      totalTransactionCosts += transactionCost;
      totalSlippage += slippageCost;

      if (tradeProfit > transactionCost) {
        profitableTrades++;
      }
    }

    const netPnL = totalSpreadCapture + totalTimingAlpha + totalMarketMovement - totalTransactionCosts - totalSlippage;

    const attribution: Attribution = {
      spreadCapture: totalSpreadCapture,
      timingAlpha: totalTimingAlpha,
      marketMovement: totalMarketMovement,
      transactionCosts: -totalTransactionCosts, // Negative cost
      slippage: -totalSlippage, // Negative cost
      netPnL
    };

    const metrics: TradeMetrics = {
      totalTrades: Math.floor(windowTrades.length / 3), // Approximate tradeable opportunities
      profitableTrades,
      winRate: windowTrades.length > 0 ? (profitableTrades / Math.floor(windowTrades.length / 3)) * 100 : 0,
      avgProfitPerTrade: Math.floor(windowTrades.length / 3) > 0 ?
        netPnL / Math.floor(windowTrades.length / 3) : 0,
      totalVolume,
      avgTradeSize: totalVolume > 0 ? totalVolume / Math.floor(windowTrades.length / 3) : 0
    };

    return { attribution, metrics };
  }, [recentTrades, currentPrice, selectedPeriod, positionSize, getTWAPForPeriod]);

  if (!attribution) {
    return (
      <div style={{
        backgroundColor: '#1e293b',
        borderRadius: 8,
        padding: 16,
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <PieChart size={16} color="#f59e0b" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            Attribution Dashboard
          </h3>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
          Analyzing trade history...
          <br />
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Need sufficient trade data and TWAP data for attribution analysis
          </span>
        </div>
      </div>
    );
  }

  const { attribution: attr, metrics } = attribution;

  const formatCurrency = (amount: number) => {
    const absAmount = Math.abs(amount);
    if (absAmount < 1000) return `${amount.toFixed(0)} TZS`;
    if (absAmount < 1000000) return `${(amount / 1000).toFixed(1)}K TZS`;
    return `${(amount / 1000000).toFixed(2)}M TZS`;
  };

  const getColorForValue = (value: number) => {
    if (value > 0) return '#10b981';
    if (value < 0) return '#ef4444';
    return '#64748b';
  };

  const getIconForValue = (value: number) => {
    if (value > 0) return <TrendingUp size={12} />;
    if (value < 0) return <TrendingDown size={12} />;
    return <Minus size={12} />;
  };

  // Calculate percentages for pie chart
  const totalPositive = Math.max(1, attr.spreadCapture + attr.timingAlpha + attr.marketMovement);
  const totalNegative = Math.abs(attr.transactionCosts + attr.slippage);

  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: 8,
      padding: 16,
      border: '1px solid #334155'
    }}>
      {/* Header with period selector */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PieChart size={16} color="#3b82f6" />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>
            Attribution Dashboard
          </h3>
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {(['5m', '15m', '1h', '4h', '1d'] as const).map(period => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              style={{
                backgroundColor: selectedPeriod === period ? '#3b82f630' : 'transparent',
                border: `1px solid ${selectedPeriod === period ? '#3b82f6' : '#334155'}`,
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 10,
                color: selectedPeriod === period ? '#3b82f6' : '#94a3b8',
                cursor: 'pointer',
                fontWeight: selectedPeriod === period ? 600 : 400
              }}
            >
              {period.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Net P&L Summary */}
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: 6,
        padding: 16,
        marginBottom: 16,
        border: `1px solid ${getColorForValue(attr.netPnL)}30`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
              Net P&L ({selectedPeriod.toUpperCase()})
            </div>
            <div style={{
              fontSize: 24,
              fontWeight: 700,
              color: getColorForValue(attr.netPnL),
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              {getIconForValue(attr.netPnL)}
              {attr.netPnL >= 0 ? '+' : ''}{formatCurrency(attr.netPnL)}
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>
              {labelForSecId(selectedSymbol)}
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {metrics.totalTrades} trades • {metrics.winRate.toFixed(0)}% win rate
            </div>
          </div>
        </div>
      </div>

      {/* Profit Breakdown */}
      {showBreakdown && (
        <div style={{
          backgroundColor: '#0f172a',
          borderRadius: 6,
          padding: 12,
          marginBottom: 16
        }}>
          <div style={{ fontSize: 12, color: '#f1f5f9', fontWeight: 600, marginBottom: 12 }}>
            Profit Sources
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Spread Capture */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#10b981'
                }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Spread Capture</span>
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: getColorForValue(attr.spreadCapture)
              }}>
                {attr.spreadCapture >= 0 ? '+' : ''}{formatCurrency(attr.spreadCapture)}
              </span>
            </div>

            {/* Timing Alpha */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6'
                }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Timing Alpha</span>
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: getColorForValue(attr.timingAlpha)
              }}>
                {attr.timingAlpha >= 0 ? '+' : ''}{formatCurrency(attr.timingAlpha)}
              </span>
            </div>

            {/* Market Movement */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b'
                }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Market Movement</span>
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: getColorForValue(attr.marketMovement)
              }}>
                {attr.marketMovement >= 0 ? '+' : ''}{formatCurrency(attr.marketMovement)}
              </span>
            </div>

            {/* Divider */}
            <div style={{ height: 1, backgroundColor: '#334155', margin: '4px 0' }} />

            {/* Transaction Costs */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#ef4444'
                }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Transaction Costs</span>
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#ef4444'
              }}>
                {formatCurrency(attr.transactionCosts)}
              </span>
            </div>

            {/* Slippage */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#64748b'
                }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Slippage</span>
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: '#64748b'
              }}>
                {formatCurrency(attr.slippage)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Trade Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 8,
        fontSize: 11
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#94a3b8', marginBottom: 2 }}>Avg Profit/Trade</div>
          <div style={{
            color: getColorForValue(metrics.avgProfitPerTrade),
            fontWeight: 600
          }}>
            {formatCurrency(metrics.avgProfitPerTrade)}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#94a3b8', marginBottom: 2 }}>Volume</div>
          <div style={{ color: '#f1f5f9', fontWeight: 600 }}>
            {metrics.totalVolume.toLocaleString()}
          </div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#94a3b8', marginBottom: 2 }}>Avg Size</div>
          <div style={{ color: '#f1f5f9', fontWeight: 600 }}>
            {metrics.avgTradeSize.toFixed(0)}
          </div>
        </div>
      </div>

      {/* Strategy note */}
      <div style={{
        marginTop: 12,
        padding: 8,
        backgroundColor: '#0f172a',
        borderRadius: 4,
        border: '1px solid #334155'
      }}>
        <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.4 }}>
          <strong style={{ color: '#3b82f6' }}>Attribution Method:</strong> Spread Capture = TWAP-based profit,
          Timing Alpha = optimal entry/exit beyond base strategy,
          Market Movement = directional market contribution.
          Based on optimized trading algorithms.
        </div>
      </div>
    </div>
  );
}